<script setup lang="ts">
import { SEARCH_FILTERS } from '~~/shared/search-constants'

/**
 * HeroSearchPill — Pill de recherche du hero V3.
 *
 * Instance unique dans HeroHomeV3 (hero en un seul DOM responsive). L'état
 * vient du composable partagé `useHomeSearch()`.
 */

interface Props {
  /**
   * Sens d'ouverture du dropdown de suggestions, transmis tel quel à
   * `HoliSearchAutocomplete`. HeroHomeV3 le fixe à l'exécution via
   * `matchMedia` : `'up'` sous md (< 768 px), car le clavier virtuel occupe
   * la moitié basse de l'écran et masque une liste ancrée sous le champ ;
   * `'down'` au-delà et en SSR.
   */
  drop?: 'down' | 'up'
}

withDefaults(defineProps<Props>(), { drop: 'down' })

const { t } = useI18n()
const search = useHomeSearch()

// Teinte du bouton de recherche selon le type actif (spec 4.74.1) : le fond
// reprend EXACTEMENT la couleur de l'option sélectionnée dans le switcher —
// encre (Tout) / sauge (CDH) / ocre (Gîte). La logique de recherche elle-même
// n'est pas modifiée.
const { accentVar, accentInkVar } = useHomeType()

// Règle de contraste partagée avec les surfaces /recherche (`accentInkClass`) :
// sur l'aplat ocre, seul un contenu encre tient l'AA — c'est déjà le choix du
// switcher pour son option Gîte active. Une seule implémentation pour éviter
// que home et recherche divergent au prochain type ajouté.
const searchButtonInkClass = computed(() => accentInkClass(accentVar.value))

/**
 * Trait de la pill teinté par le type actif (Tout / CDH / Gîte).
 *
 * On part de la variante ENCRE du type, pas de la couleur de marque : l'ocre
 * gîte brut plafonne à 2,4:1 contre le fond paper-50, sous le minimum de 3:1
 * exigé pour un composant non textuel. Diluée à 80 %, l'encre de type donne
 * ~8,2 / 5,4 / 3,7 (Tout / CDH / Gîte) — visible sans agresser, là où l'ancien
 * `hairline` (14 %) disparaissait. Au focus, couleur pleine.
 *
 * Le gîte est le cas contraignant : à 70 % il ne tenait le seuil que de 0,1.
 * Si `--color-type-gite-ink` est un jour éclairci, revérifier ce ratio.
 */
const pillBorderColor = computed(() =>
  `color-mix(in oklab, ${accentInkVar.value} 80%, transparent)`,
)

/**
 * Trait du segment destination (visible en mobile uniquement, où il forme une
 * mini-pill dans la pill). Volontairement plus léger que l'enveloppe : à teinte
 * égale, les deux cadres concentriques se concurrencent au lieu de hiérarchiser.
 */
const innerBorderColor = computed(() =>
  `color-mix(in oklab, ${accentInkVar.value} 30%, transparent)`,
)

// Label dynamique du bouton dates dans la pill
const datesLabel = computed(() => {
  if (!search.localDateRange.value) return t('pages.home.heroV3.searchDatesLabel')
  const fmt = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short' })
  const [sy, sm, sd] = search.localDateRange.value.start.split('-').map(Number)
  const [ey, em, ed] = search.localDateRange.value.end.split('-').map(Number)
  return `${fmt.format(new Date(sy!, sm! - 1, sd!))} — ${fmt.format(new Date(ey!, em! - 1, ed!))}`
})

const guestsLabel = computed(() => {
  const total = search.localAdults.value + search.localChildren.value
  if (total === 0) return t('pages.home.heroV3.searchGuestsLabel')
  return t('common.search.adultsCount', { count: search.localAdults.value })
    + (search.localChildren.value > 0 ? `, ${t('common.search.childrenCount', { count: search.localChildren.value })}` : '')
})
</script>

<template>
  <form
    role="search"
    :aria-label="$t('pages.home.heroV3.searchAriaLabel')"
    class="w-full"
    @submit.prevent="search.handleSearch"
  >
    <div
      class="search-pill relative flex flex-col gap-3 sm:gap-0 sm:flex-row sm:items-center bg-paper-50 rounded-2xl sm:rounded-xl border-2 shadow-md p-3 sm:p-0 transition-colors"
      :style="{ '--pill-accent': accentInkVar, 'borderColor': pillBorderColor }"
    >
      <!-- Segment Destination (mobile: mini-pill autonome / desktop: segment de la pill) -->
      <div
        class="flex-1 sm:min-w-0 flex items-center border rounded-lg sm:border-0 sm:rounded-none sm:pl-6 sm:pr-2"
        :style="{ borderColor: innerBorderColor }"
      >
        <HoliSearchAutocomplete
          v-model="search.searchQuery.value"
          :placeholder="$t('pages.home.heroV3.searchPlaceholder')"
          :aria-label="$t('pages.home.heroV3.searchAriaLabel')"
          size="lg"
          variant="none"
          :drop="drop"
          class="flex-1"
          @select="search.handleSuggestionSelect"
        />
      </div>

      <!-- Separators + popover triggers -->
      <UPopover v-model:open="search.advancedOpen.value" class="sm:flex-none sm:flex sm:items-center">
        <div class="flex items-stretch gap-0 w-full sm:w-auto">
          <span class="hidden sm:block w-px h-8 bg-paper-900/15 self-center" aria-hidden="true" />
          <button
            type="button"
            class="flex-1 sm:flex-none px-3 sm:px-5 py-2 sm:py-2.5 text-center sm:text-left text-sm text-paper-900/80 hover:text-paper-900 hover:bg-sage-100 rounded-lg transition-colors cursor-pointer"
          >
            <span class="tag text-paper-900/60 mb-0.5 block justify-center sm:justify-start">{{ $t('pages.home.heroV3.searchDatesLabel') }}</span>
            <span class="font-sans text-sm truncate block">{{ datesLabel }}</span>
          </button>
          <span class="block w-px h-8 bg-paper-900/15 self-center" aria-hidden="true" />
          <button
            type="button"
            class="flex-1 sm:flex-none px-3 sm:px-5 py-2 sm:py-2.5 text-center sm:text-left text-sm text-paper-900/80 hover:text-paper-900 hover:bg-sage-100 rounded-lg transition-colors relative cursor-pointer"
          >
            <span class="tag text-paper-900/60 mb-0.5 block justify-center sm:justify-start">{{ $t('pages.home.heroV3.searchGuestsLabel') }}</span>
            <span class="font-sans text-sm truncate block">{{ guestsLabel }}</span>
            <span
              v-if="search.persistedCriteriaCount.value > 0"
              class="absolute top-0 right-0 sm:-top-1 sm:-right-1 min-w-5 h-5 inline-flex items-center justify-center font-mono text-[11px] tracking-[0.08em] bg-ochre text-paper-50 rounded-full px-1.5"
              aria-hidden="true"
            >
              {{ search.persistedCriteriaCount.value }}
            </span>
          </button>
        </div>

        <template #content>
          <div class="p-4 w-80 space-y-4">
            <!-- Résumé des critères actifs -->
            <p
              v-if="search.advancedSummary.value"
              class="text-xs text-sage-700 bg-sage-50 rounded-md px-2 py-1"
            >
              {{ search.advancedSummary.value }}
            </p>

            <!-- Dates de séjour -->
            <div>
              <LazyHoliDateRangePicker
                v-model="search.localDateRange.value"
                context="search"
                :min-date="search.todayISO"
                :highlight="search.dateHintVisible.value"
              >
                <template #fallback>
                  <USkeleton class="h-10 w-full rounded-md" />
                </template>
              </LazyHoliDateRangePicker>
              <p
                v-if="search.dateHintVisible.value"
                class="text-xs text-sage-700 flex items-center gap-1 mt-1"
              >
                <UIcon
                  name="i-lucide-calendar"
                  class="w-3.5 h-3.5 shrink-0"
                />
                {{ $t('common.search.dateHintInline') }}
              </p>
            </div>

            <!-- Voyageurs -->
            <div class="space-y-3">
              <div class="flex items-center justify-between">
                <span class="text-sm text-paper-900/80">{{ $t('common.search.adults') }}</span>
                <HoliGuestCounter
                  :model-value="search.localAdults.value"
                  :max="SEARCH_FILTERS.MAX_ADULTS"
                  :input-label="$t('common.search.adultsInputAriaLabel')"
                  :decrement-label="$t('common.search.decreaseAdults')"
                  :increment-label="$t('common.search.increaseAdults')"
                  @update:model-value="search.setAdults($event)"
                />
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm text-paper-900/80">{{ $t('common.search.children') }}</span>
                <HoliGuestCounter
                  :model-value="search.localChildren.value"
                  :max="SEARCH_FILTERS.MAX_CHILDREN"
                  :input-label="$t('common.search.childrenInputAriaLabel')"
                  :decrement-label="$t('common.search.decreaseChildren')"
                  :increment-label="$t('common.search.increaseChildren')"
                  @update:model-value="search.setChildren($event)"
                />
              </div>
              <div
                v-if="search.showChildrenAges.value"
                class="space-y-2 pt-2 border-t border-paper-200"
              >
                <p class="text-xs text-paper-900/60">
                  {{ $t('common.search.childrenAgesLabel') }}
                </p>
                <div class="grid grid-cols-2 gap-2">
                  <USelect
                    v-for="i in search.localChildren.value"
                    :key="i"
                    :model-value="search.localChildrenAges.value[i - 1] ?? 5"
                    :items="search.childAgeOptions.value"
                    value-key="value"
                    size="xs"
                    :aria-label="$t('common.search.childAgeAriaLabel', { index: i })"
                    @update:model-value="search.updateChildAge(i - 1, $event as number)"
                  />
                </div>
              </div>
            </div>

            <div class="flex gap-2 pt-2 border-t border-paper-200">
              <UButton
                v-if="search.persistedCriteriaCount.value > 0"
                variant="ghost"
                size="sm"
                color="neutral"
                class="flex-1"
                @click="search.clearAdvancedCriteria"
              >
                {{ $t('pages.home.hero.advancedSearchClear') }}
              </UButton>
              <UButton
                size="sm"
                color="neutral"
                class="type-accent-btn flex-1"
                :class="searchButtonInkClass"
                :style="{ '--type-accent': accentVar }"
                icon="i-lucide-search"
                @click="search.handleAdvancedSearch"
              >
                {{ $t('pages.home.heroV3.searchButton') }}
              </UButton>
            </div>
          </div>
        </template>
      </UPopover>

      <!-- Bouton principal : mobile = pill centré compact / desktop = icone-only ronde -->
      <div class="sm:p-2 flex justify-center">
        <UButton
          type="submit"
          size="lg"
          color="neutral"
          :class="['type-accent-btn px-8 h-12 sm:w-12 sm:h-12 sm:px-0 rounded-full justify-center cursor-pointer', searchButtonInkClass, { 'search-pulse': search.searchPulse.value }]"
          :style="{ '--type-accent': accentVar }"
          :aria-label="$t('pages.home.heroV3.searchButton')"
        >
          <UIcon name="i-lucide-search" class="size-5" />
          <span class="sm:hidden ml-2">{{ $t('pages.home.heroV3.searchButton') }}</span>
        </UButton>
      </div>
    </div>
  </form>
</template>

<style scoped>
/* Trait de la pill : couleur pleine du type actif dès qu'un champ est focalisé.
   `!important` car la couleur au repos est appliquée en style inline. */
.search-pill:focus-within {
  border-color: var(--pill-accent) !important;
}

/* Pulse de la loupe quand une suggestion vient d'être sélectionnée :
   indique visuellement à l'utilisateur qu'il doit cliquer pour valider la recherche.
   Désactivé via prefers-reduced-motion. */
/* La teinte du bouton principal vient désormais de l'utilitaire global
   `button.type-accent-btn` (main.css), alimenté par `--type-accent` en inline.
   L'ancienne règle `search-btn-gite`, qui ne couvrait que le mode gîte, a été
   retirée avec la spec 4.74.1. */

.search-pulse {
  animation: search-pulse 1.6s ease-in-out infinite;
}

@keyframes search-pulse {
  0%, 100% {
    box-shadow: 0 0 0 0 color-mix(in oklab, var(--type-accent, var(--color-sage-600, #4f6c45)) 50%, transparent);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 0 12px color-mix(in oklab, var(--type-accent, var(--color-sage-600, #4f6c45)) 0%, transparent);
    transform: scale(1.06);
  }
}

@media (prefers-reduced-motion: reduce) {
  .search-pulse {
    animation: none;
    box-shadow: 0 0 0 3px color-mix(in oklab, var(--type-accent, var(--color-sage-600, #4f6c45)) 40%, transparent);
  }
}
</style>
